/**
 * 🟦 V14.4 Paso 1 — useLyonChat: hook centralizado del chat de Lyon.
 *
 * Extraído de `LyonHeaderButton.jsx` (líneas ~155-158 + 737-828 + 830-851).
 *
 * Encapsula:
 *  - Estado: messages, input, sending, sessionIdRef, copiedId, applyState
 *  - Handlers: handleSend (POST /api/lyon/chat con node_context + vision),
 *              handleCopyMessage (clipboard + feedback temporal),
 *              handleDeleteMessage (filtra por id).
 *
 * Deps externas (pasadas como params al hook): contexto del Builder
 * (selection, attach flags, multistep), screenshot util, memoria multi-paso,
 * idioma, API base, setter de capturing-shot.
 *
 * Mantiene EXACTAMENTE la lógica original (incluyendo el comentario V11.0
 * Phase 7 + V11.1) — refactor PURO sin cambios semánticos.
 */
import { useState, useRef, useCallback } from 'react';
import { showSuccess } from '../../../utils/showSuccess';

export function useLyonChat({
  es,
  API,
  builderSelection,
  attachContext,
  attachVision,
  multiStep,
  setCapturingShot,
  getBuilderIframe,
  captureNodeScreenshot,
  rememberUserAction,
  hasImplicitReference,
  getMemorySnapshot,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);
  const [applyState, setApplyState] = useState({});

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;

    // 🟥 CUADRO NEGRO 2026-07-02 — Auto-Visual Creation: detectar verbo antes
    // del chat regular. Si matchea, disparar el modal y NO llamar al chat.
    try {
      const detectRes = await fetch(`${API}/lyon/detect-verb`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (detectRes.ok) {
        const detectData = await detectRes.json();
        if (detectData.matched && detectData.verb) {
          // Registrar el mensaje del usuario en el chat y una respuesta AVC.
          const userMsg = {
            id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            role: 'user',
            content: text,
          };
          const avcMsg = {
            id: `a-${Date.now()}-avc-${Math.random().toString(36).slice(2, 6)}`,
            role: 'assistant',
            content: es
              ? `🧩 Detecté que querés crear un **${detectData.verb.verb}**. Abrí el diálogo para elegir Skeleton o IA.`
              : `🧩 I detected you want to create a **${detectData.verb.verb}**. Opened the dialog to pick Skeleton or AI.`,
            avc_verb_id: detectData.verb.id,
          };
          setMessages((prev) => [...prev, userMsg, avcMsg]);
          setInput('');
          rememberUserAction(text);
          window.dispatchEvent(new CustomEvent('damyxia:auto-visual-open', {
            detail: { verb: detectData.verb, userText: text },
          }));
          return;
        }
      }
    } catch (e) {
      // Silent fail — detectar verbo es opcional, continuar con chat normal.
      console.warn('[useLyonChat] detect-verb failed, continuing chat:', e);
    }

    const userMsg = {
      id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      role: 'user',
      content: text,
    };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setInput('');
    setSending(true);
    rememberUserAction(text);
    try {
      let nodeContext = null;
      let imageB64 = null;
      let usedMemory = false;
      if (builderSelection && attachContext) {
        nodeContext = builderSelection;
        if (attachVision) {
          const iframeEl = getBuilderIframe();
          if (iframeEl) {
            try {
              setCapturingShot(true);
              imageB64 = await captureNodeScreenshot(iframeEl, builderSelection.uib_id);
            } catch {
              imageB64 = null;
            } finally {
              setCapturingShot(false);
            }
          }
        }
      } else if (multiStep && hasImplicitReference(text)) {
        const snap = getMemorySnapshot();
        if (snap.lastNodeContext && snap.lastNodeId) {
          nodeContext = snap.lastNodeContext;
          imageB64 = snap.lastScreenshot;
          usedMemory = true;
        }
      }
      const res = await fetch(`${API}/lyon/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: sessionIdRef.current,
          history: messages,
          language: es ? 'es' : 'en',
          node_context: nodeContext,
          image_b64: imageB64,
        }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.session_id) sessionIdRef.current = data.session_id;
      setMessages((prev) => [...prev, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        role: 'assistant',
        content: data.reply,
        targetNodeContext: nodeContext || null,
        usedMemory,
      }]);
      // 🟥 CUADRO NEGRO 2026-06-27 — Toast verde bilingüe según resultado.
      // - Si el backend detectó un verbo de acción → "Elemento visual creado".
      // - Caso normal → "Acción completada".
      // El idioma sale del cookie/localStorage (no del prop `es`) para
      // garantizar coherencia con la última preferencia del usuario.
      if (data.action_detected) {
        showSuccess('visual_created');
      } else {
        showSuccess('action_completed');
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: 'assistant',
          content: es
            ? '⚠️ No pude responder ahora mismo. Intenta de nuevo en unos segundos.'
            : '⚠️ I could not reply right now. Please try again in a moment.',
          error: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  }, [input, messages, sending, es, builderSelection, attachContext, attachVision, multiStep,
      API, setCapturingShot, getBuilderIframe, captureNodeScreenshot,
      rememberUserAction, hasImplicitReference, getMemorySnapshot]);

  const handleCopyMessage = useCallback(async (msg) => {
    try {
      await navigator.clipboard.writeText(msg.content);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = msg.content;
      ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopiedId(msg.id);
    setTimeout(() => setCopiedId((cur) => (cur === msg.id ? null : cur)), 1200);
  }, []);

  const handleDeleteMessage = useCallback((msgId) => {
    setMessages((prev) => prev.filter((m) => m.id !== msgId));
  }, []);

  return {
    messages, setMessages,
    input, setInput,
    sending, setSending,
    sessionIdRef,
    copiedId,
    applyState, setApplyState,
    handleSend,
    handleCopyMessage,
    handleDeleteMessage,
  };
}

export default useLyonChat;
